// Reads user data from the host page's `window.__courtsapp_user` global. The
// widget is bundled into a shadow root and embedded on pages that already have
// Better Auth — we never run auth ourselves. Returns null when the global isn't
// set or no user is signed in (anonymous fallback).
//
// The host app (courtsapp web) sets this global from its AuthContextProvider
// whenever the session state changes. Same pattern as the old Clerk integration
// (which read window.Clerk), just a different global name.

export type UserData = {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phoneNumber: string | null;
};

type CourtsappUser = {
  id?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
};

declare global {
  interface Window {
    __courtsapp_user?: CourtsappUser | null;
  }
}

function extractUser(): UserData | null {
  const u = window.__courtsapp_user;
  if (!u?.id || !u?.firstName || !u?.email) {
    return null;
  }
  return {
    id: u.id,
    firstName: u.firstName,
    lastName: u.lastName ?? "",
    fullName: [u.firstName, u.lastName].filter(Boolean).join(" "),
    email: u.email,
    phoneNumber: u.phoneNumber ?? null,
  };
}

// Reads from window.__courtsapp_user live on every call — never caches. The
// host page can sign the user in/out at any time and we want the next session
// to pick up the new state without a widget reload.
export function getUser(): UserData | null {
  return extractUser();
}

export function isAuthenticated(): boolean {
  const user = getUser();
  return !!(user?.firstName && user?.email);
}
